import React from 'react';
import { useTranslations } from 'react-ui';
import type { EntryMode } from '../../types';

interface EntryModeSelectorProps {
  mode: EntryMode;
  onChange: (mode: EntryMode) => void;
}

const MODES: Array<{ id: EntryMode; labelKey: string; icon: string }> = [
  { id: 'form', labelKey: 'sc_entry_form', icon: '📝' },
  { id: 'ai-text', labelKey: 'sc_entry_ai_text', icon: '✨' },
  { id: 'ai-poster', labelKey: 'sc_entry_ai_poster', icon: '📷' },
];

/**
 * 3-segment pill control for choosing how to start event creation.
 * Only rendered in 'form' | 'ai-text' | 'ai-poster' modes (hidden for 'ai-multi-result').
 */
export const EntryModeSelector: React.FC<EntryModeSelectorProps> = ({ mode, onChange }) => {
  const { translated } = useTranslations();

  return (
    <div
      role="tablist"
      aria-label={translated('sc_entry_mode_label')}
      className="flex rounded-lg bg-gray-100 p-1 gap-1"
    >
      {MODES.map(({ id, labelKey, icon }) => {
        const isSelected = mode === id;
        return (
          <button
            key={id}
            role="tab"
            aria-selected={isSelected}
            onClick={() => onChange(id)}
            className={`
              flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-sm font-medium
              transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500
              ${isSelected
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}
            `}
          >
            <span aria-hidden="true">{icon}</span>
            <span>{translated(labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
};
